import { oc } from "@orpc/contract";
import { z } from "zod";

const CompanyLocationSchema = z.object({
  id: z.string(),
  companyCode: z.string(),
  companyName: z.string().nullable(),
  divisionCode: z.string(),
  divisionName: z.string().nullable(),
  locationCode: z.string(),
  description: z.string(),
  customerNumber: z.string().nullable(),
  branchShortCode: z.string().nullable(),
  abbBranchCode: z.string().nullable(),
  taxProvince: z.string().nullable(),
  vdmBranchCode: z.string().nullable(),
  glAccountType1: z.string().nullable(),
  glAccountType2: z.string().nullable(),
  glAccountType3: z.string().nullable(),
  glAccountDebit: z.string().nullable(),
  glAccount849RejectedType1: z.string().nullable(),
  glAccount849RejectedType2: z.string().nullable(),
  glAccount849RejectedType3: z.string().nullable(),
});

const ListResponseSchema = z.object({
  locations: z.array(CompanyLocationSchema),
});

const UpdateInputSchema = z.object({
  id: z.string().uuid(),
  companyCode: z.string().optional(),
  companyName: z.string().nullable().optional(),
  divisionCode: z.string().optional(),
  divisionName: z.string().nullable().optional(),
  locationCode: z.string().optional(),
  description: z.string().optional(),
  customerNumber: z.string().nullable().optional(),
  branchShortCode: z.string().nullable().optional(),
  abbBranchCode: z.string().nullable().optional(),
  taxProvince: z.string().nullable().optional(),
  vdmBranchCode: z.string().nullable().optional(),
  glAccountType1: z.string().nullable().optional(),
  glAccountType2: z.string().nullable().optional(),
  glAccountType3: z.string().nullable().optional(),
  glAccountDebit: z.string().nullable().optional(),
  glAccount849RejectedType1: z.string().nullable().optional(),
  glAccount849RejectedType2: z.string().nullable().optional(),
  glAccount849RejectedType3: z.string().nullable().optional(),
});

const CreateInputSchema = z.object({
  companyCode: z.string(),
  companyName: z.string().nullable().optional(),
  divisionCode: z.string(),
  divisionName: z.string().nullable().optional(),
  locationCode: z.string(),
  description: z.string(),
  customerNumber: z.string().nullable().optional(),
  branchShortCode: z.string().nullable().optional(),
  abbBranchCode: z.string().nullable().optional(),
  taxProvince: z.string().nullable().optional(),
  vdmBranchCode: z.string().nullable().optional(),
  glAccountType1: z.string().nullable().optional(),
  glAccountType2: z.string().nullable().optional(),
  glAccountType3: z.string().nullable().optional(),
  glAccountDebit: z.string().nullable().optional(),
  glAccount849RejectedType1: z.string().nullable().optional(),
  glAccount849RejectedType2: z.string().nullable().optional(),
  glAccount849RejectedType3: z.string().nullable().optional(),
});

const DeleteInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * Org-wide JE template defaults for GL posting Excel downloads. Stored in
 * org_module_config.config.shipAndDebit.sd_gl_je_template. Applies to every
 * division's JE Template sheet header (divisionName still comes from
 * sd_company_locations).
 */
const JeTemplateConfigSchema = z.object({
  transType: z.string(),
  userId: z.string(),
  userEmail: z.string(),
  sourceCode: z.string(),
  // Per-division email overrides keyed by division key (companyCode +
  // divisionCode, e.g. "W+", "QO"). Empty object when none configured.
  userEmailByDivision: z.record(z.string(), z.string()),
});

const UpdateJeTemplateInputSchema = z.object({
  transType: z.string().max(50),
  userId: z.string().max(50),
  // Allows a single address or a semicolon-separated list — not strictly an email.
  userEmail: z.string().max(255),
  sourceCode: z.string().max(50),
  // Per-division email overrides; blank entries are dropped on save.
  userEmailByDivision: z.record(z.string(), z.string().max(255)).default({}),
});

/**
 * Per-division shipping province for NO-TAX VDMs, keyed by division key
 * (companyCode + divisionCode, e.g. "W+", "QE"). Stored in
 * org_module_config.config.shipAndDebit.sd_vdm_no_tax_province_by_division.
 */
const NoTaxProvincesSchema = z.object({
  provincesByDivision: z.record(z.string(), z.string()),
});

const UpdateNoTaxProvincesInputSchema = z.object({
  // Province codes (e.g. ON, QC, MB). Blank entries are dropped on save.
  provincesByDivision: z.record(z.string(), z.string().max(10)).default({}),
});

/**
 * Who to email when AS400 rejects an 844 batch file (see `as400ErrorDetection`).
 * Stored in org_module_config.config.shipAndDebit.sd_844_error_notification_emails.
 * An empty list is valid — the alert then goes to the S&D team inbox.
 */
const ErrorNotificationEmailsSchema = z.object({
  emails: z.array(z.string()),
});

const UpdateErrorNotificationEmailsInputSchema = z.object({
  /**
   * De-duplicated on save. Each entry must be a real address: an invalid one is
   * non-empty, so it would pass the "nothing configured" fallback and be handed
   * to the mail provider as an unusable recipient — the alert would then fail
   * for everyone on the list, which is the outcome this feature exists to avoid.
   *
   * `.pipe(z.email())` rather than `z.email().max(255)`: chaining `.max` onto
   * the top-level format helper is not supported in this Zod version.
   */
  emails: z
    .array(z.string().trim().max(255).pipe(z.email()))
    .max(20)
    .default([]),
});

export const companyLocationsContracts = {
  list: oc.output(ListResponseSchema).route({
    summary: "List ship-and-debit company locations",
    description:
      "Lists the organization's ship-and-debit branch locations with company, division, location codes, customer number, tax province, VDM branch code, and GL accounts.",
    method: "GET",
  }),
  create: oc.input(CreateInputSchema).output(CompanyLocationSchema).route({
    summary: "Create a ship-and-debit company location",
    description:
      "Adds a branch location with company, division, and location codes plus optional tax province, VDM branch code, and GL posting accounts.",
    method: "POST",
  }),
  update: oc.input(UpdateInputSchema).output(CompanyLocationSchema).route({
    summary: "Update a ship-and-debit company location",
    description:
      "Edits a branch location's codes, names, customer number, tax province, VDM branch code, or GL posting accounts by id.",
    method: "POST",
  }),
  delete: oc
    .input(DeleteInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a ship-and-debit company location",
      description:
        "Removes a branch location from the ship-and-debit company hierarchy by id.",
      method: "POST",
    }),
  getJeTemplate: oc.output(JeTemplateConfigSchema).route({
    summary: "Get GL journal entry template defaults",
    description:
      "Returns the organization-wide journal entry header defaults (transaction type, user id, email, source code, per-division email overrides) used in GL posting Excel downloads.",
    method: "GET",
  }),
  updateJeTemplate: oc
    .input(UpdateJeTemplateInputSchema)
    .output(JeTemplateConfigSchema)
    .route({
      summary: "Update GL journal entry template defaults",
      description:
        "Saves the organization-wide journal entry header defaults for GL posting Excel downloads, including per-division user email overrides.",
      method: "POST",
    }),
  getNoTaxProvinces: oc.output(NoTaxProvincesSchema).route({
    summary: "Get no-tax VDM provinces by division",
    description:
      "Returns the shipping province used for no-tax vendor debit memos, keyed by company and division.",
    method: "GET",
  }),
  updateNoTaxProvinces: oc
    .input(UpdateNoTaxProvincesInputSchema)
    .output(NoTaxProvincesSchema)
    .route({
      summary: "Update no-tax VDM provinces by division",
      description:
        "Saves the shipping province code used for no-tax vendor debit memos for each company and division; blank entries are dropped.",
      method: "POST",
    }),
  getErrorNotificationEmails: oc.output(ErrorNotificationEmailsSchema).route({
    summary: "Get 844 rejection alert recipients",
    description:
      "Returns the email addresses alerted when the ERP rejects an 844 claim batch file; an empty list means alerts go to the ship-and-debit team inbox.",
    method: "GET",
  }),
  updateErrorNotificationEmails: oc
    .input(UpdateErrorNotificationEmailsInputSchema)
    .output(ErrorNotificationEmailsSchema)
    .route({
      summary: "Update 844 rejection alert recipients",
      description:
        "Saves up to 20 de-duplicated email addresses to alert when the ERP rejects an 844 claim batch file.",
      method: "POST",
    }),
} as const;
